import React from 'react';
import { Card, Button } from "react-bootstrap";
import styles from '../SessionHistoryDetailView.module.css';
import { formatDateOnly } from '../../utils/formatDateOnly';

const CarePlanConcernsSection = ({
  sessionFormData,
  updateFormData,
  clientGoalError,
  objectivesError,
  interventionsError,
  sessionFrequencyError,
  reviewDateError,
  setClientGoalError,
  setObjectivesError,
  setInterventionsError,
  setSessionFrequencyError,
  setReviewDateError,
  autoResizeTextarea,
  isFollowUp = false,
  carePlanEditMode = null,
  onUpdateClick,
  onCreateNewClick,
  onCancelEdit,
  onSave,
  saving = false
}) => {
  const isEditMode = carePlanEditMode === "update" || carePlanEditMode === "create";
  const hasExistingData = sessionFormData.clientGoal || sessionFormData.objectives || sessionFormData.interventions;

  return (
    <Card id="care-plan" className={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h3 className={styles.sectionHeading} style={{ marginBottom: "0" }}>
          Care Plan
        </h3>
        {isFollowUp && !isEditMode && (
          <div style={{ display: "flex", gap: "8px" }}>
            {hasExistingData && (
              <Button
                variant="success"
                className={styles.saveButton}
                onClick={onUpdateClick}
              >
                Update
              </Button>
            )}
            <Button
              variant="success"
              className={styles.saveButton}
              onClick={onCreateNewClick}
            >
              Create New
            </Button>
          </div>
        )}
        {isFollowUp && isEditMode && (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="primary"
              className={styles.signSubmitButton}
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="primary"
              className={styles.signSubmitButton}
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label className={styles.labelWithMargin}>
            Client Goal
          </label>
          {isEditMode && (
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#8E8E93" }}>
              {sessionFormData.clientGoal?.length || 0}/500
            </div>
          )}
        </div>

        {isFollowUp && !isEditMode ? (
          <div style={{
            padding: "12px",
            backgroundColor: "#FAFAFA",
            borderRadius: "4px",
            minHeight: "72px",
            fontSize: "16px",
            color: "#000",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            textAlign: "left"
          }}>
            {sessionFormData.clientGoal || "No client goal entered"}
          </div>
        ) : (
          <>
            <textarea
              value={sessionFormData.clientGoal || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 500) {
                  updateFormData("clientGoal", value);
                  if (clientGoalError && value.trim()) {
                    setClientGoalError("");
                  }
                }
              }}
              placeholder="Free Text"
              className={styles.textarea}
              maxLength={500}
            />
            {clientGoalError && (
              <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                {clientGoalError}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label className={styles.labelWithMargin}>
            Objectives
          </label>
          {isEditMode && (
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#8E8E93" }}>
              {sessionFormData.objectives?.length || 0}/500
            </div>
          )}
        </div>

        {isFollowUp && !isEditMode ? (
          <div style={{
            padding: "12px",
            backgroundColor: "#FAFAFA",
            borderRadius: "4px",
            minHeight: "72px",
            fontSize: "16px",
            color: "#000",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            textAlign: "left"
          }}>
            {sessionFormData.objectives || "No objectives entered"}
          </div>
        ) : (
          <>
            <textarea
              value={sessionFormData.objectives || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 500) {
                  updateFormData("objectives", value);
                  if (objectivesError && value.trim()) {
                    setObjectivesError("");
                  }
                }
              }}
              placeholder="Free Text"
              className={styles.textarea}
              maxLength={500}
            />

            {objectivesError && (
              <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                {objectivesError}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label className={styles.labelWithMargin}>
            Interventions
          </label>
          {isEditMode && (
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#8E8E93" }}>
              {sessionFormData.interventions?.length || 0}/500
            </div>
          )}
        </div>

        {isFollowUp && !isEditMode ? (
          <div style={{
            padding: "12px",
            backgroundColor: "#FAFAFA",
            borderRadius: "4px",
            minHeight: "72px",
            fontSize: "16px",
            color: "#000",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            textAlign: "left"
          }}>
            {sessionFormData.interventions || "No interventions entered"}
          </div>
        ) : (
          <>
            <textarea
              value={sessionFormData.interventions || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 500) {
                  updateFormData("interventions", value);
                  if (interventionsError && value.trim()) {
                    setInterventionsError("");
                  }
                }
              }}
              placeholder="Free Text"
              className={styles.textarea}
              maxLength={500}
            />
            {interventionsError && (
              <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                {interventionsError}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0", whiteSpace: "nowrap" }}>
            Frequency:
          </label>
          {isFollowUp && !isEditMode ? (
            <div style={{
              padding: "8px 12px",
              backgroundColor: "#FAFAFA",
              borderRadius: "4px",
              fontSize: "16px",
              color: "#000",
              textAlign: "left"
            }}>
              {sessionFormData.sessionFrequency === "Other" 
                ? (sessionFormData.otherSessionFrequencyText || "No frequency specified")
                : (sessionFormData.sessionFrequency || "No frequency selected")
              }
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "end", gap: "8px", justifyContent: "space-between", flex: "1" }}>
              <select
                value={sessionFormData.sessionFrequency || ""}
                onChange={(e) => {
                  updateFormData("sessionFrequency", e.target.value);
                  if (sessionFrequencyError) {
                    setSessionFrequencyError("");
                  }
                  if (e.target.value !== "Other") {
                    updateFormData("otherSessionFrequencyText", "");
                  }
                }}
                className={styles.selectMediumWidth}
              >
                <option value="">Select frequency</option>
                <option value="Weekly">Weekly</option>
                <option value="Biweekly">Biweekly</option>
                <option value="Monthly">Monthly</option>
                <option value="As needed">As needed</option>
                <option value="Other">Other</option>
              </select>
              {sessionFormData.sessionFrequency === "Other" && (
                <div style={{ fontSize: "12px", color: "#8E8E93", whiteSpace: "nowrap" }}>
                  {sessionFormData.otherSessionFrequencyText?.length || 0}/100
                </div>
              )}
            </div>
          )}
        </div>
        {!isFollowUp || isEditMode ? (
          <>
            {sessionFormData.sessionFrequency === "Other" && (
              <div style={{ marginTop: "12px", marginLeft: "0" }}>
                <textarea
                  id="other-Session-Frequency-Text"
                  value={sessionFormData.otherSessionFrequencyText || ""}
                  onChange={(e) => {
                    autoResizeTextarea(e.target);
                    updateFormData("otherSessionFrequencyText", e.target.value);
                  }}
                  placeholder="Please specify."
                  className={styles.otherTextInput}
                  rows={1}
                  style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
                  maxLength={100}
                />
              </div>
            )}
            {sessionFrequencyError && (
              <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                {sessionFrequencyError}
              </div>
            )}
          </>
        ) : null}
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0", whiteSpace: "nowrap" }}>
            Review Date
          </label>
          {isFollowUp && !isEditMode ? (
            <div style={{
              padding: "8px 12px",
              backgroundColor: "#FAFAFA",
              borderRadius: "4px",
              fontSize: "16px",
              color: "#000",
              textAlign: "left"
            }}>
              {sessionFormData.reviewDate ? formatDateOnly(sessionFormData.reviewDate) : "No review date set"}
            </div>
          ) : (
            <input
              type="date"
              value={sessionFormData.reviewDate || ""}
              onChange={(e) => {
                updateFormData("reviewDate", e.target.value);
                if (reviewDateError) {
                  setReviewDateError("");
                }
              }}
              min={(() => {
                // When in "create" mode, set minimum date to +180 days from now
                if (carePlanEditMode === "create") {
                  const today = new Date();
                  const minDate = new Date(today);
                  minDate.setDate(today.getDate() + 180);
                  return minDate.toISOString().split('T')[0];
                }
                return undefined;
              })()}
              className={`${styles.selectMediumWidth} ${styles.dateInput}`}
              style={{
                padding: "8px 12px",
                fontSize: "16px",
                border: "none",
                borderRadius: "4px",
                outline: "none"
              }}
            />
          )}
        </div>
        {reviewDateError && isEditMode && (
          <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
            {reviewDateError}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CarePlanConcernsSection;

